'use strict';

import axios from 'axios';
import utils from './utils';
import partnerAccess from './partnerAccess';

/**
 * ユーザ情報を取得する
 * リクエストパラメタはヘッダーに入っている
 * @returns {Object} - user data
 */
async function getUser() {
  utils.consoleLog('[getUser] Start getUser API');

  let profile = null;
  try {
    const url = `${process.env.VUE_APP_DOMAIN_API}/v1/user`;
    profile = await axios.get(url, utils.setHeaderWithToken());
  } catch (err) {
    utils.consoleLog('[getUser] API Error');
    return null;
  }

  // partnerListの形を整える
  profile.data.partnerList = utils.parsePartnerList(profile.data.partnerList);

  return profile.data;
}

/**
 * パートナー情報を取得する
 * @param partnerId {String} - lineId of partner
 * @returns {Object} - user data
 */
async function getPartner (partnerId) {
  utils.consoleLog('[getPartner] Start getPartner API');

  // パートナーの取得
  let partner = null;
  try {
    const url = `${process.env.VUE_APP_DOMAIN_API}/v1/partner/${partnerId}`;
    partner = await axios.get(url, utils.setHeaderWithAuthorization());
  } catch (err) {
    utils.consoleLog('[getPartner] API Error');
    return null;
  }

  return partner.data;
}

/**
 * 思い出一覧を取得する
 * @param userId {String} - userId of login user
 * @returns {Array} - memory list
 */
async function getMemories (userId) {
  utils.consoleLog('[getMemories] Start getMemories API');

  let memories = null;
  try {
    const url = `${process.env.VUE_APP_DOMAIN_API}/v1/memories/${userId}`;
    memories = await axios.get(url, utils.setHeaderWithAuthorization());
  } catch (err) {
    utils.consoleLog('[getMemories] API Error');
    return null;
  }

  return memories.data;
}

export default {
  /**
   * サービスに必要な全てのデータを取得する
   * @param profile {Object} - profile of login user
   * @returns {Object} - user, parner, memory list
   */
  getAllData: async profile => {
    // ユーザの取得
    if (!profile.userId) {
      profile = await getUser();
    }

    // 連携しているパートナーを全員取得
    let partners = [];
    if (profile && profile.partnerList) {
      let tasks = Object.keys(profile.partnerList).map(partnerId => getPartner(partnerId));
      partners = await Promise.all(tasks);
    }

    // 現在選択しているuserIdに紐づく思い出を取得する
    let memories = null;
    if (profile && profile.userId) {
      const currentUserId = partnerAccess.getCurrentUserId(profile);
      memories = await getMemories(currentUserId);
    }

    return {
      profile: profile,
      partners: partners,
      memories: memories,
    };
  },
  authorization: async code => {
    utils.consoleLog('[authorization] Start authorization API');

    let res = null;
    try {
      const url = `${process.env.VUE_APP_DOMAIN_API}/v1/authorization/${code}`;
      res = await axios.post(url, {}, utils.setHeader());
    } catch (err) {
      utils.consoleLog('[authorization] API Error');
      return null;
    }

    // partnerListの形を整える
    res.data.partnerList = utils.parsePartnerList(res.data.partnerList);

    return res.data;
  },
  putMemory: async (userId, params) => {
    utils.consoleLog('[putMemory] Start putMemory API');

    try {
      const url = `${process.env.VUE_APP_DOMAIN_API}/v1/memory/${userId}`;
      // TODO:Authorizationヘッダーをつけて認証通るようにする
      await axios.post(url, params, utils.setHeader());
      return true;
    } catch (err) {
      utils.consoleLog('[putMemory] API Error');
      return false;
    }
  },
  deleteMemory: async (userId, memoryId) => {
    utils.consoleLog('[deleteMemory] Start deleteMemory API');

    try {
      const url = `${process.env.VUE_APP_DOMAIN_API}/v1/memory/${userId}/${memoryId}`;
      // TODO:Authorizationヘッダーをつけて認証通るようにする
      await axios.delete(url, utils.setHeader());
      return true;
    } catch (err) {
      utils.consoleLog('[deleteMemory] API Error');
      return false;
    }
  },
  deletePartner: async (lineId, partnerId, userId) => {
    utils.consoleLog('[deletePartner] Start deletePartner API');

    try {
      const url = `${process.env.VUE_APP_DOMAIN_API}/v1/partner/${lineId}/${partnerId}/${userId}`;
      // TODO:Authorizationヘッダーをつけて認証通るようにする
      await axios.delete(url, utils.setHeader());
      return true;
    } catch (err) {
      utils.consoleLog('[deletePartner] API Error');
      return false;
    }
  },
  deleteUser: async lineId => {
    utils.consoleLog('[deleteUser] Start deleteUser API');

    try {
      const url = `${process.env.VUE_APP_DOMAIN_API}/v1/user/${lineId}`;
      // TODO:Authorizationヘッダーをつけて認証通るようにする
      await axios.delete(url, utils.setHeader());
      return true;
    } catch (err) {
      utils.consoleLog('[deleteUser] API Error');
      return false;
    }
  },
  getPartner: async lineId => {
    utils.consoleLog('[getPartner] Start getPartner API');

    let res = null;
    try {
      const url = `${process.env.VUE_APP_DOMAIN_API}/v1/partner/${lineId}`;
      res = await axios.get(url, utils.setHeader());
    } catch (err) {
      utils.consoleLog('[getPartner] API Error');
      return null;
    }

    return res.data;
  },
  putPartner: async (lineId, partnerId) => {
    utils.consoleLog('[putPartner] Start putPartner API');

    let profile = null;
    if (!lineId) {
      profile = await getUser();
    }
    const reqLineId = lineId ? lineId : profile.lineId;

    try {
      const url = `${process.env.VUE_APP_DOMAIN_API}/v1/partner/${reqLineId}/${partnerId}`;
      // TODO:Authorizationヘッダーをつけて認証通るようにする
      await axios.post(url, {}, utils.setHeader());
      return true;
    } catch (err) {
      utils.consoleLog('[putPartner] API Error');
      return false;
    }
  },
  userAlertPut: async (lineId, alertName, alertValue) => {
    utils.consoleLog('[userAlertPut] Start userAlertPut API');

    const req = {
      alertName: alertName,
      alertValue: alertValue,
    };

    try {
      const url = `${process.env.VUE_APP_DOMAIN_API}/v1/user/alert/${lineId}`;
      // TODO:Authorizationヘッダーをつけて認証通るようにする
      await axios.post(url, req, utils.setHeader());
      return true;
    } catch (err) {
      utils.consoleLog('[userAlertPut] API Error');
      return false;
    }
  },
};
